import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Snackbar, Alert, AlertTitle } from '@mui/material';
import { useAuth } from './AuthContext';
import eventService from '../services/eventService';

const NotificationContext = createContext();

// Production configuration
const NOTIFICATION_CONFIG = {
  MAX_NOTIFICATIONS: 100,
  CLEANUP_INTERVAL: 5 * 60 * 1000, // 5 minutes
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  CONNECTION_TIMEOUT: 30000
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notification, setNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const subscriptionRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const cleanupIntervalRef = useRef(null);

  // Cleanup old notifications periodically
  const cleanupOldNotifications = useCallback(() => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    setNotifications(prev => {
      const filtered = prev.filter(n => new Date(n.createdAt) > oneWeekAgo);
      return filtered.slice(0, NOTIFICATION_CONFIG.MAX_NOTIFICATIONS);
    });
  }, []);

  // Enhanced notification handler with deduplication
  const handleNewNotification = useCallback((newNotification) => {
    if (!newNotification?.id) return;
    
    setNotifications(prev => {
      // Prevent duplicates
      if (prev.some(n => n.id === newNotification.id)) {
        return prev;
      }
      
      const updated = [newNotification, ...prev].slice(0, NOTIFICATION_CONFIG.MAX_NOTIFICATIONS);
      return updated;
    });
    
    if (!newNotification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  // Retry connection with exponential backoff
  const retryConnection = useCallback((attempt = 1) => {
    if (attempt > NOTIFICATION_CONFIG.RETRY_ATTEMPTS) {
      setConnectionStatus('failed');
      return;
    }

    const delay = NOTIFICATION_CONFIG.RETRY_DELAY * Math.pow(2, attempt - 1);
    retryTimeoutRef.current = setTimeout(() => {
      if (user?.id && isAuthenticated) {
        try {
          subscriptionRef.current = eventService.subscribeToNotifications(user.id, handleNewNotification);
          setConnectionStatus('connected');
        } catch (error) {
          console.error(`Notification connection attempt ${attempt} failed:`, error);
          retryConnection(attempt + 1);
        }
      }
    }, delay);
  }, [user?.id, isAuthenticated, handleNewNotification]);

  // Real-time notification subscription with error handling
  useEffect(() => {
    if (!user?.id || !isAuthenticated) {
      setConnectionStatus('disconnected');
      return;
    }

    setConnectionStatus('connecting');
    
    try {
      subscriptionRef.current = eventService.subscribeToNotifications(user.id, handleNewNotification);
      setConnectionStatus('connected');
      
      // Setup cleanup interval
      cleanupIntervalRef.current = setInterval(cleanupOldNotifications, NOTIFICATION_CONFIG.CLEANUP_INTERVAL);
      
    } catch (error) {
      console.error('Failed to establish notification subscription:', error);
      setConnectionStatus('error');
      retryConnection();
    }

    return () => {
      if (subscriptionRef.current) {
        eventService.unsubscribe(`notifications_${user.id}`);
        subscriptionRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
      setConnectionStatus('disconnected');
    };
  }, [user?.id, isAuthenticated, handleNewNotification, retryConnection, cleanupOldNotifications]);

  // Production-ready notification display with validation
  const showNotification = useCallback((message, severity = 'info', title = null, duration = 6000) => {
    if (!message || typeof message !== 'string') {
      console.warn('Invalid notification message:', message);
      return;
    }
    
    // Sanitize message for XSS prevention
    const sanitizedMessage = message.replace(/<script[^>]*>.*?<\/script>/gi, '').substring(0, 500);
    
    setNotification({ 
      message: sanitizedMessage, 
      severity, 
      title: title?.substring(0, 100), 
      duration: Math.min(Math.max(duration, 1000), 30000) // 1-30 seconds
    });
  }, []);

  const showSuccess = useCallback((message, title = null) => showNotification(message, 'success', title), [showNotification]);
  const showError = useCallback((message, title = null) => showNotification(message, 'error', title, 8000), [showNotification]);
  const showWarning = useCallback((message, title = null) => showNotification(message, 'warning', title, 7000), [showNotification]);
  const showInfo = useCallback((message, title = null) => showNotification(message, 'info', title), [showNotification]);

  const hideNotification = useCallback(() => setNotification(null), []);
  
  // Production notification management with error handling
  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    
    try {
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === id ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showError('Failed to mark notification as read');
    }
  }, []);
  
  const markAllAsRead = useCallback(async () => {
    try {
      setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showError('Failed to mark all notifications as read');
    }
  }, []);
  
  const clearNotifications = useCallback(async () => {
    try {
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error clearing notifications:', error);
      showError('Failed to clear notifications');
    }
  }, []);

  const sendNotification = useCallback(async (userID, type, message, metadata = null) => {
    if (!userID || !type || !message) {
      throw new Error('Missing required notification parameters');
    }
    
    if (!isAuthenticated) {
      throw new Error('Authentication required to send notifications');
    }
    
    try {
      return await eventService.sendNotification(userID, type, message, metadata);
    } catch (error) {
      console.error('Error sending notification:', error);
      showError('Failed to send notification');
      throw error;
    }
  }, [isAuthenticated]);

  return (
    <NotificationContext.Provider value={{
      // Display functions
      showNotification,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      hideNotification,
      
      // State
      notifications,
      unreadCount,
      connectionStatus,
      
      // Actions
      markAsRead,
      markAllAsRead,
      clearNotifications,
      sendNotification,
      
      // Utilities
      isConnected: connectionStatus === 'connected',
      hasNotifications: notifications.length > 0
    }}>
      {children}
      
      <Snackbar
        open={!!notification}
        autoHideDuration={notification?.duration}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {notification && (
          <Alert 
            onClose={hideNotification} 
            severity={notification.severity}
            variant="filled"
            sx={{ minWidth: 300 }}
          >
            {notification.title && <AlertTitle>{notification.title}</AlertTitle>}
            {notification.message}
          </Alert>
        )}
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};